'use client';

import { ChevronDownIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { playSfx } from '@/hooks/use-sound';
import { type Event, trackEvent } from '@/lib/events';
import { cn } from '@/lib/utils';

import { BeforeAfterMinibars, type MiniBar } from './before-after-minibars';

export type AccordionItem = {
  id: string;
  /** Conclusion-style heading; the row's main text. */
  name: string;
  /** Compact value chip, right-aligned on the closed row. */
  win: string;
  /** Lead sentence of the open panel, in foreground weight. */
  analogy: string;
  paragraphs: string[];
  /** Optional interactive visual rendered under the copy. */
  visual?: React.ReactNode;
  /** Optional before/after pair; replays on every open when present. */
  bars?: MiniBar[];
};

/**
 * Single-open disclosure list shared by both studies. The first row opens
 * itself when the section scrolls into view so the pattern is obvious, and
 * minibars (where an item has them) replay on every open.
 */
export function DetailAccordion({
  items,
  eventName,
  eventKey = 'item',
}: {
  items: AccordionItem[];
  /** Which closed-enum analytics event to emit on open. */
  eventName: Extract<
    Event['name'],
    'case_study_leak_open' | 'case_study_mechanism_open'
  >;
  /** Property name carrying the opened item's id. */
  eventKey?: string;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  // Per-item counters: opening one row must not replay every other row's bars.
  const [replayKeys, setReplayKeys] = useState<Record<string, number>>({});
  const autoOpenedRef = useRef(false);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !autoOpenedRef.current) {
          autoOpenedRef.current = true;
          observer.disconnect();
          setOpenId((current) => {
            const id = current ?? items[0].id;
            setReplayKeys((keys) => ({ ...keys, [id]: (keys[id] ?? 0) + 1 }));
            return id;
          });
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [items]);

  const toggle = (id: string) => {
    autoOpenedRef.current = true;
    const isOpening = openId !== id;
    playSfx(isOpening ? 'open' : 'close');
    setOpenId(isOpening ? id : null);
    if (isOpening) {
      setReplayKeys((keys) => ({ ...keys, [id]: (keys[id] ?? 0) + 1 }));
      trackEvent({ name: eventName, properties: { [eventKey]: id } });
    }
  };

  return (
    <div ref={listRef}>
      {items.map((item) => {
        const open = openId === item.id;

        return (
          <article
            key={item.id}
            className="border-b border-edge last:border-b-0"
          >
            <button
              type="button"
              aria-expanded={open}
              aria-controls={`accordion-panel-${item.id}`}
              data-sfx-managed=""
              onClick={() => toggle(item.id)}
              className={cn(
                'flex w-full items-center gap-4 px-4 py-4 text-left',
                'transition-colors hover:bg-accent2',
                open && 'bg-accent2/50'
              )}
            >
              <span className="min-w-0 flex-1 text-base font-semibold text-balance sm:text-lg">
                {item.name}
              </span>
              <span className="hidden shrink-0 font-mono text-xs text-muted-foreground tabular-nums sm:inline">
                {item.win}
              </span>
              <ChevronDownIcon
                aria-hidden
                className={cn(
                  'size-4 shrink-0 text-muted-foreground transition-transform duration-300',
                  open && 'rotate-180'
                )}
              />
            </button>

            <div
              id={`accordion-panel-${item.id}`}
              className={cn(
                'grid transition-[grid-template-rows] duration-300 ease-out',
                open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
              )}
            >
              {/* Clipping to zero height leaves the panel in the a11y tree,
                  so a closed row would still be read out in full. `inert`
                  removes it from the tree and from tab order. */}
              <div className="overflow-hidden" inert={!open}>
                <div className="px-4 pb-6">
                  <p className="mb-3 max-w-[60ch] text-base font-medium">
                    {item.analogy}
                  </p>
                  {item.paragraphs.map((paragraph) => (
                    <p
                      key={paragraph.slice(0, 32)}
                      className="mb-3 max-w-[60ch] text-sm text-muted-foreground last:mb-0"
                    >
                      {paragraph}
                    </p>
                  ))}
                  <p className="mt-3 font-mono text-xs text-muted-foreground sm:hidden">
                    {item.win}
                  </p>

                  {item.visual}

                  {item.bars && (
                    <BeforeAfterMinibars
                      replayKey={replayKeys[item.id] ?? 0}
                      className="mt-5"
                      bars={item.bars}
                    />
                  )}
                </div>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}
